import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, RefreshCw, ChevronRight, ChevronDown, X } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";
import * as XLSX from "xlsx";

const GROUPS = ["10-20", "21-30", "31-45", "45+"];
const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const REASON_CLS = {
  "Not GRN'd": "bg-amber-100 text-amber-800",
  "Short GRN": "bg-orange-100 text-orange-800",
  "Need to pick": "bg-blue-100 text-blue-700",
  "Paid — mark Delivered": "bg-emerald-100 text-emerald-800",
  "Return raised": "bg-purple-100 text-purple-800",
  "Blinkit item not mapped to a SKU": "bg-zinc-200 text-zinc-700",
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysBetween = (a, b) => Math.round((new Date(a) - new Date(b)) / 86400000);

// FOLLOW-UP 4 (1c): every filter lives in the URL, so Back returns to the same list
const FILTER_KEYS = ["q", "channels", "customer", "grp", "status", "dfrom", "dto", "dmin", "dmax",
                     "vmin", "vmax", "sku", "expiry", "rep", "view"];

export default function StockOnHoldPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [failed, setFailed] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState({});
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const f = useCallback((k, d = "") => params.get(k) ?? d, [params]);
  // Typed filters are held locally and written to the URL once typing stops, so no letter is
  // lost and Back does not step through every key press.
  const [draft, setDraft] = useState({});
  const timer = useRef(null);
  const write = useCallback((k, v, replace) => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      if (v === "" || v === null || v === undefined) next.delete(k); else next.set(k, v);
      return next;
    }, { replace });
  }, [setParams]);
  const setF = (k, v) => write(k, v, false);
  const setText = (k, v) => {
    setDraft(d => ({ ...d, [k]: v }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      write(k, v, true);
      setDraft(d => { const n = { ...d }; delete n[k]; return n; });
    }, 300);
  };
  const t = (k) => (draft[k] !== undefined ? draft[k] : f(k));
  const clearFilters = () => {
    clearTimeout(timer.current);
    setDraft({});
    setParams(prev => {
      const next = new URLSearchParams(prev);
      FILTER_KEYS.filter(k => k !== "view").forEach(k => next.delete(k));
      return next;
    }, { replace: false });
  };

  const view = f("view", "order");                 // FOLLOW-UP 4 (1e): Order view is the default
  const channels = (f("channels") ? f("channels").split("|") : []);

  const load = useCallback(async () => {
    setLoading(true); setFailed("");
    try {
      const r = await api.get("/reports/stock-on-hold", { timeout: 60000 });
      setData(r.data.data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else setFailed("The report is taking too long / could not load — try again.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allRows = useMemo(() => data?.rows || [], [data]);

  const rows = useMemo(() => {
    const q = f("q").trim().toLowerCase();
    const grp = f("grp"), status = f("status"), customer = f("customer"), sku = f("sku");
    const dfrom = f("dfrom"), dto = f("dto"), expiry = f("expiry"), rep = f("rep");
    const dmin = f("dmin"), dmax = f("dmax"), vmin = f("vmin"), vmax = f("vmax");
    const t = todayISO();
    return allRows.filter(r => {
      if (q && !`${r.order_number} ${r.po_number} ${r.customer}`.toLowerCase().includes(q)) return false;
      if (channels.length && !channels.includes(r.channel || "Unknown")) return false;
      if (customer && (r.customer || "") !== customer) return false;
      if (grp && !(r.in_total && r.ageing_group === grp)) return false;
      if (status && (r.status || "") !== status) return false;
      if (rep && (r.sales_rep_name || "Unassigned") !== rep) return false;
      if (sku && (r.sku_name || r.sku_id || "") !== sku) return false;
      if (dfrom && (r.dispatch_date || "") < dfrom) return false;
      if (dto && (r.dispatch_date || "9999") > dto) return false;
      if (dmin !== "" && Number(r.days || 0) < Number(dmin)) return false;
      if (dmax !== "" && Number(r.days || 0) > Number(dmax)) return false;
      if (vmin !== "" && Number(r.value || 0) < Number(vmin)) return false;
      if (vmax !== "" && Number(r.value || 0) > Number(vmax)) return false;
      if (expiry === "expired" && !(r.expiry_date && r.expiry_date < t)) return false;
      if (expiry === "soon" && !(r.expiry_date && r.expiry_date >= t && daysBetween(r.expiry_date, t) <= 30)) return false;
      if (expiry === "unknown" && r.expiry_date) return false;
      return true;
    });
  }, [allRows, params]); // eslint-disable-line react-hooks/exhaustive-deps

  // the cards, the ageing line and the by-channel line all follow the same filtered rows (1d)
  const view1 = useMemo(() => {
    const inTotal = rows.filter(r => r.in_total);
    const byGroup = {}; const byChannel = {};
    inTotal.forEach(r => {
      const g = byGroup[r.ageing_group] || (byGroup[r.ageing_group] = { orders: 0, qty: 0, value: 0 });
      g.orders += 1; g.qty += Number(r.qty_dispatched || 0); g.value += Number(r.value || 0);
      const c = byChannel[r.channel || "Unknown"] || (byChannel[r.channel || "Unknown"] = { orders: 0, qty: 0, value: 0 });
      c.orders += 1; c.qty += Number(r.qty_dispatched || 0); c.value += Number(r.value || 0);
    });
    const side = (g) => {
      const picked = rows.filter(r => r.group === g);
      return { lines: picked.length, orders: new Set(picked.map(r => r.order_number)).size,
               value: picked.reduce((s, r) => s + Number(r.value || 0), 0) };
    };
    return {
      total_value: inTotal.reduce((s, r) => s + Number(r.value || 0), 0),
      total_rows: inTotal.length, byGroup, byChannel,
      paid: side("Paid — mark Delivered"), ret: side("Return raised"),
      not_mapped: rows.filter(r => r.group === "Not mapped").length,
    };
  }, [rows]);

  // Order view — one row per order with its SKU lines underneath (1e)
  const orderRows = useMemo(() => {
    const byOrder = new Map();
    rows.forEach(r => {
      const key = r.order_number || `—|${r.sku_id}`;
      const o = byOrder.get(key) || {
        order_number: r.order_number, order_id: r.order_id, po_number: r.po_number,
        customer: r.customer, channel: r.channel, sales_rep_name: r.sales_rep_name,
        status: r.status, dispatch_date: r.dispatch_date, days: r.days, group: r.group,
        reasons: new Set(), skus: 0, value: 0, lines: [],
      };
      o.reasons.add(r.reason); o.skus += 1; o.value += Number(r.value || 0); o.lines.push(r);
      byOrder.set(key, o);
    });
    return [...byOrder.values()].sort((a, b) => (b.days || 0) - (a.days || 0));
  }, [rows]);

  const uniq = (fn) => [...new Set(allRows.map(fn).filter(Boolean))].sort();

  // the download is built from exactly the rows on screen (1d — chosen: client-side)
  const download = () => {
    setDownloading(true);
    try {
      const out = rows.map(r => ({
        "Order No.": r.order_number, "PO No.": r.po_number, Customer: r.customer,
        Channel: r.channel, "Sales Rep": r.sales_rep_name || "", Status: r.status,
        "Dispatch Date": fmtDate(r.dispatch_date), Days: r.days, Ageing: r.ageing_group,
        SKU: r.sku_id, "SKU Name": r.sku_name, "Qty Dispatched": r.qty_dispatched,
        "Qty GRN'd": r.qty_grn, Batch: r.batch_number, "MFG Date": fmtDate(r.mfg_date),
        "Expiry Date": fmtDate(r.expiry_date), "Expiry unknown because": r.expiry_unknown_because || "",
        "Value (INR, excl GST)": r.value_known === false ? "price missing" : r.value,
        "In total": r.in_total ? "Yes" : "No", Group: r.group, Reason: r.reason,
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(out), "Stock on Hold");
      XLSX.writeFile(wb, "ORBIT2_stock_on_hold.xlsx");
      toast.success(`${out.length} line(s) downloaded — the same rows as on screen`);
    } catch (e) { toast.error("Could not build the file"); }
    finally { setDownloading(false); }
  };

  if (denied) return <div className="p-6" data-testid="stock-on-hold-denied"><p className="text-sm text-zinc-500">You do not have access to the Stock on Hold report.</p></div>;
  if (loading) return <div className="p-6"><p className="text-xs text-zinc-400">Loading…</p></div>;
  if (failed) return (
    <div className="p-6" data-testid="stock-on-hold-page">
      <div className="border border-amber-300 bg-amber-50 rounded-sm p-6 text-center" data-testid="soh-timeout">
        <p className="text-xs text-amber-800 mb-3">{failed}</p>
        <Button variant="outline" className="rounded-sm h-8 text-xs" onClick={load} data-testid="soh-retry">
          <RefreshCw size={14} className="mr-1" /> Retry
        </Button>
      </div>
    </div>
  );

  const activeCount = FILTER_KEYS.filter(k => k !== "view" && params.get(k)).length;

  return (
    <div className="p-6" data-testid="stock-on-hold-page">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-950">Stock on Hold</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Stock dispatched 10 days ago or more with no GRN yet, Blinkit POs received short, and lines Blinkit wants us to collect. Every value is excl GST. As of {fmtDate(data?.as_of)}.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-sm border border-zinc-300 overflow-hidden" data-testid="soh-view-switch">
            {["order", "sku"].map(v => (
              <button key={v} onClick={() => setF("view", v)} data-testid={`soh-view-${v}`}
                      className={`px-3 h-8 text-xs ${view === v ? "bg-zinc-900 text-white" : "bg-white text-zinc-600"}`}>
                {v === "order" ? "Order view" : "SKU view"}
              </button>
            ))}
          </div>
          <Button variant="outline" className="rounded-sm h-8 text-xs" onClick={download} disabled={downloading} data-testid="stock-on-hold-export">
            <Download size={14} className="mr-1" /> {downloading ? "Preparing…" : "Excel"}
          </Button>
        </div>
      </div>

      {/* ── filters (1c) ── */}
      <div className="border border-zinc-200 rounded-sm p-3 mb-3" data-testid="soh-filters">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <Input placeholder="Order no. / PO / customer" value={t("q")} onChange={e => setText("q", e.target.value)}
                 className="h-8 text-xs rounded-sm col-span-2" data-testid="soh-filter-search" />
          <select multiple={false} value={channels[0] || ""} onChange={e => setF("channels", e.target.value)}
                  className="h-8 text-xs rounded-sm border border-zinc-300 px-2" data-testid="soh-filter-channel">
            <option value="">All channels</option>
            {uniq(r => r.channel).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={f("customer")} onChange={e => setF("customer", e.target.value)}
                  className="h-8 text-xs rounded-sm border border-zinc-300 px-2" data-testid="soh-filter-customer">
            <option value="">All customers</option>
            {uniq(r => r.customer).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={f("grp")} onChange={e => setF("grp", e.target.value)}
                  className="h-8 text-xs rounded-sm border border-zinc-300 px-2" data-testid="soh-filter-group">
            <option value="">All ageing</option>
            {GROUPS.map(g => <option key={g} value={g}>{g} days</option>)}
          </select>
          <select value={f("status")} onChange={e => setF("status", e.target.value)}
                  className="h-8 text-xs rounded-sm border border-zinc-300 px-2" data-testid="soh-filter-status">
            <option value="">All statuses</option>
            {uniq(r => r.status).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={f("rep")} onChange={e => setF("rep", e.target.value)}
                  className="h-8 text-xs rounded-sm border border-zinc-300 px-2" data-testid="soh-filter-rep">
            <option value="">All sales reps</option>
            {uniq(r => r.sales_rep_name || "Unassigned").map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={f("sku")} onChange={e => setF("sku", e.target.value)}
                  className="h-8 text-xs rounded-sm border border-zinc-300 px-2" data-testid="soh-filter-sku">
            <option value="">All SKUs</option>
            {uniq(r => r.sku_name || r.sku_id).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={f("expiry")} onChange={e => setF("expiry", e.target.value)}
                  className="h-8 text-xs rounded-sm border border-zinc-300 px-2" data-testid="soh-filter-expiry">
            <option value="">Any expiry</option>
            <option value="expired">Expired</option>
            <option value="soon">Within 30 days</option>
            <option value="unknown">Unknown</option>
          </select>
          <Input type="date" value={t("dfrom")} onChange={e => setText("dfrom", e.target.value)}
                 className="h-8 text-xs rounded-sm" data-testid="soh-filter-date-from" />
          <Input type="date" value={t("dto")} onChange={e => setText("dto", e.target.value)}
                 className="h-8 text-xs rounded-sm" data-testid="soh-filter-date-to" />
          <Input type="number" placeholder="Days min" value={t("dmin")} onChange={e => setText("dmin", e.target.value)}
                 className="h-8 text-xs rounded-sm" data-testid="soh-filter-days-min" />
          <Input type="number" placeholder="Days max" value={t("dmax")} onChange={e => setText("dmax", e.target.value)}
                 className="h-8 text-xs rounded-sm" data-testid="soh-filter-days-max" />
          <Input type="number" placeholder="Value min" value={t("vmin")} onChange={e => setText("vmin", e.target.value)}
                 className="h-8 text-xs rounded-sm" data-testid="soh-filter-value-min" />
          <Input type="number" placeholder="Value max" value={t("vmax")} onChange={e => setText("vmax", e.target.value)}
                 className="h-8 text-xs rounded-sm" data-testid="soh-filter-value-max" />
        </div>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-[10px] text-zinc-500" data-testid="soh-filter-count">
            {rows.length} of {allRows.length} line(s){activeCount ? ` · ${activeCount} filter(s) on` : ""}
          </span>
          {activeCount > 0 && (
            <button onClick={clearFilters} className="text-[10px] text-zinc-600 underline flex items-center gap-1" data-testid="soh-clear-filters">
              <X size={10} /> Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3" data-testid="stock-on-hold-cards">
        <div className="border border-zinc-200 p-3 rounded-sm">
          <p className="text-[10px] uppercase text-zinc-500">Total on hold (excl GST)</p>
          <p className="text-lg font-mono font-bold text-zinc-900" data-testid="soh-total">{fmt(view1.total_value)}</p>
          <p className="text-[10px] text-zinc-400">{view1.total_rows} lines</p>
        </div>
        {GROUPS.map(g => {
          const v = view1.byGroup[g] || { orders: 0, qty: 0, value: 0 };
          return (
            <button key={g} onClick={() => setF("grp", f("grp") === g ? "" : g)} data-testid={`soh-group-${g}`}
                    className={`border p-3 rounded-sm text-left transition-colors ${f("grp") === g ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
              <p className="text-[10px] uppercase text-zinc-500">{g} days</p>
              <p className="text-lg font-mono font-bold text-zinc-900">{fmt(v.value)}</p>
              <p className="text-[10px] text-zinc-400">{v.orders} lines · {v.qty} units</p>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="soh-side-cards">
        <div className="border border-zinc-200 p-2 rounded-sm" data-testid="soh-paid">
          <p className="text-[10px] uppercase text-zinc-500">Paid — mark Delivered</p>
          <p className="text-sm font-mono font-bold text-zinc-800">{fmt(view1.paid.value)}</p>
          <p className="text-[9px] text-zinc-400">{view1.paid.orders} orders · not in the total</p>
        </div>
        <div className="border border-zinc-200 p-2 rounded-sm" data-testid="soh-return">
          <p className="text-[10px] uppercase text-zinc-500">Return raised</p>
          <p className="text-sm font-mono font-bold text-zinc-800">{fmt(view1.ret.value)}</p>
          <p className="text-[9px] text-zinc-400">{view1.ret.orders} orders · not in the total</p>
        </div>
        <div className="border border-zinc-200 p-2 rounded-sm" data-testid="soh-not-mapped">
          <p className="text-[10px] uppercase text-zinc-500">Not mapped</p>
          <p className="text-sm font-mono font-bold text-zinc-800">{view1.not_mapped}</p>
          <p className="text-[9px] text-zinc-400">Blinkit items with no SKU · price missing</p>
        </div>
        <div className="border border-zinc-200 p-2 rounded-sm" data-testid="soh-dn-unmatched">
          <p className="text-[10px] uppercase text-zinc-500">DN lines not matched</p>
          <p className="text-sm font-mono font-bold text-zinc-800">{data?.dn_unmatched_count || 0}</p>
          <p className="text-[9px] text-zinc-400">no ORBIT order or SKU · not rows</p>
        </div>
      </div>

      {view === "order" ? (
        <div className="border border-zinc-200 rounded-sm overflow-auto" data-testid="soh-order-view">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-left">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-semibold [&>th]:text-zinc-600 whitespace-nowrap">
                <th></th><th>Order</th><th>PO</th><th>Customer</th><th>Channel</th><th>Sales Rep</th>
                <th>Status</th><th>Dispatched</th><th className="text-right">Days</th><th>Group</th>
                <th>Reason(s)</th><th className="text-right">SKUs</th><th className="text-right">Value (excl GST)</th>
              </tr>
            </thead>
            <tbody>
              {orderRows.length === 0 ? <tr><td colSpan={13} className="text-center py-8 text-zinc-400">Nothing on hold</td></tr>
                : orderRows.map((o) => (
                  <Fragment key={o.order_number || o.order_id}>
                    <tr key={o.order_number} className={`border-t border-zinc-100 [&>td]:px-3 [&>td]:py-2 whitespace-nowrap ${o.group === "On hold" ? "" : "bg-zinc-50/60"}`}
                        data-testid={`soh-order-row-${o.order_number || "none"}`}>
                      <td>
                        <button onClick={() => setOpen(p => ({ ...p, [o.order_number]: !p[o.order_number] }))}
                                data-testid={`soh-expand-${o.order_number || "none"}`}>
                          {open[o.order_number] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </button>
                      </td>
                      <td className="font-mono font-semibold">
                        {o.order_id ? (
                          <a href={`/orders/${o.order_id}`} className="underline hover:text-zinc-600"
                             onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(`/orders/${o.order_id}`); }}
                             data-testid={`soh-order-link-${o.order_number}`}>{o.order_number}</a>
                        ) : (o.order_number || "—")}
                      </td>
                      <td className="font-mono text-[10px]">{o.po_number || "—"}</td>
                      <td>{o.customer || "—"}</td>
                      <td>{o.channel || "—"}</td>
                      <td>{o.sales_rep_name || "—"}</td>
                      <td>{o.status || "—"}</td>
                      <td className="font-mono">{fmtDate(o.dispatch_date) || "—"}</td>
                      <td className="text-right font-mono font-semibold">{o.days}</td>
                      <td className="text-[10px]">{o.group}</td>
                      <td>{[...o.reasons].map(rr => (
                        <span key={rr} className={`px-1.5 py-0.5 mr-1 text-[10px] font-semibold rounded-sm ${REASON_CLS[rr] || "bg-zinc-100 text-zinc-700"}`}>{rr}</span>
                      ))}</td>
                      <td className="text-right font-mono">{o.skus}</td>
                      <td className="text-right font-mono">{fmt(o.value)}</td>
                    </tr>
                    {open[o.order_number] && o.lines.map((r, i) => (
                      <tr key={`${o.order_number}-${i}`} className="border-t border-zinc-50 bg-zinc-50/40 [&>td]:px-3 [&>td]:py-1 text-[11px]">
                        <td></td><td colSpan={5} title={r.sku_name}>{r.sku_name || r.sku_id}</td>
                        <td colSpan={2}>Batch {r.batch_number || "—"} · MFG {fmtDate(r.mfg_date) || "—"}</td>
                        <td colSpan={2}>Expiry {r.expiry_date ? fmtDate(r.expiry_date) : `— (${r.expiry_unknown_because || "unknown"})`}</td>
                        <td>{r.reason}</td>
                        <td className="text-right font-mono">{r.qty_dispatched} out{r.qty_grn == null ? "" : ` · ${r.qty_grn} GRN'd`}</td>
                        <td className="text-right font-mono">{r.value_known === false ? "price missing" : fmt(r.value)}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-sm overflow-auto" data-testid="soh-sku-view">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-left">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-semibold [&>th]:text-zinc-600 whitespace-nowrap">
                <th>Order</th><th>PO</th><th>Customer</th><th>Channel</th><th>Sales Rep</th><th>Dispatched</th><th className="text-right">Days</th>
                <th>SKU</th><th className="text-right">Qty out</th><th className="text-right">Qty GRN&apos;d</th>
                <th>Batch</th><th>MFG</th><th>Expiry</th><th className="text-right">Value (excl GST)</th><th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={15} className="text-center py-8 text-zinc-400">Nothing on hold</td></tr>
                : rows.map((r, i) => (
                  <tr key={i} className={`border-t border-zinc-100 [&>td]:px-3 [&>td]:py-2 whitespace-nowrap ${r.in_total ? "" : "bg-zinc-50/60"}`} data-testid={`soh-row-${r.order_number}-${r.sku_id}`}>
                    <td className="font-mono font-semibold">
                      {r.order_id ? (
                        <a href={`/orders/${r.order_id}`} className="underline hover:text-zinc-600"
                           onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(`/orders/${r.order_id}`); }}
                           data-testid={`soh-sku-order-link-${r.order_number}`}>{r.order_number}</a>
                      ) : (r.order_number || "—")}
                    </td>
                    <td className="font-mono text-[10px]">{r.po_number || "—"}</td>
                    <td>{r.customer || "—"}</td>
                    <td>{r.channel || "—"}</td>
                    <td>{r.sales_rep_name || "—"}</td>
                    <td className="font-mono">{fmtDate(r.dispatch_date) || "—"}</td>
                    <td className="text-right font-mono font-semibold">{r.days}</td>
                    <td title={r.sku_name}>{r.sku_name || r.sku_id || "—"}</td>
                    <td className="text-right font-mono">{r.qty_dispatched}</td>
                    <td className="text-right font-mono">{r.qty_grn == null ? "—" : r.qty_grn}</td>
                    <td className="font-mono text-[10px]">{r.batch_number || "—"}</td>
                    <td className="font-mono text-[10px]">{fmtDate(r.mfg_date) || "—"}</td>
                    <td className="font-mono text-[10px]" title={r.expiry_unknown_because}>{r.expiry_date ? fmtDate(r.expiry_date) : (r.expiry_unknown_because ? `— (${r.expiry_unknown_because})` : "—")}</td>
                    <td className="text-right font-mono">{r.value_known === false ? <span className="text-zinc-400">price missing</span> : fmt(r.value)}</td>
                    <td><span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-sm ${REASON_CLS[r.reason] || "bg-zinc-100 text-zinc-700"}`}>{r.reason}</span></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.dn_unmatched_count > 0 && (
        <p className="text-[10px] text-zinc-500 mt-2" data-testid="soh-dn-note">
          {data.dn_unmatched_count} DN line{data.dn_unmatched_count === 1 ? "" : "s"} could not be matched to an ORBIT order and SKU.
        </p>
      )}

      {Object.keys(view1.byChannel).length > 0 && (
        <div className="mt-4 border border-zinc-200 rounded-sm p-3" data-testid="soh-by-channel">
          <p className="text-[10px] uppercase text-zinc-500 mb-2">By channel (on hold, excl GST)</p>
          <div className="flex flex-wrap gap-4">
            {Object.entries(view1.byChannel).sort((a, b) => b[1].value - a[1].value).map(([c, v]) => (
              <div key={c} className="text-xs"><span className="text-zinc-500">{c}: </span>
                <span className="font-mono font-semibold">{fmt(v.value)}</span>
                <span className="text-zinc-400"> ({v.orders} lines)</span></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
